import { useMemo } from 'react';
import type { Resolver } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';

import { useAppContext } from '@/frontend/src/contexts/appContext';
import LocationSearch from '@/frontend/src/components/LocationSearch';
import ClosestLocationSearch from '@/frontend/src/components/ClosestLocationSearch';
import ClosestLocationResult from '@/frontend/src/components/ClosestLocationResult';
import type { TFormValues } from '@/frontend/src/types/context';
import { EActiveView } from '@/frontend/src/types/context';
import { NAME_SPACE } from '@/frontend/src/hooks/useLoadTranslations';
import {
    EPostalCodeValidationError,
    validatePostalCode,
} from '@/frontend/src/helpers/postalCodeValidation';

const getActiveView = (activeView: EActiveView) => {
    switch (activeView) {
        case EActiveView.LocationSearch:
            return <LocationSearch />;
        case EActiveView.ClosestLocationSearch:
            return <ClosestLocationSearch />;
        case EActiveView.ClosestLocationResult:
            return <ClosestLocationResult />;
        default:
            return <LocationSearch />;
    }
};

interface IResolverError {
    type: string;
    message: string;
}

const AppContent = () => {
    const { formValues, activeView, portal } = useAppContext();
    const { t } = useTranslation(NAME_SPACE);

    const required = {
        type: 'required',
        message: t('common.error.required'),
    };

    const invalid = {
        type: 'format',
        message: t('common.error.invalid'),
    };

    const dromTomNotSupported = {
        type: 'format',
        message: t('common.fields.postalCode.error.dromTomNotSupported'),
    };

    const resolver: Resolver<TFormValues> = (values) => {
        const postalCodeValidationError = validatePostalCode(
            values.postalCode,
            portal,
        );

        let postalCodeFormatError: Record<string, IResolverError> = {};

        switch (postalCodeValidationError) {
            case EPostalCodeValidationError.DromTomNotSupported:
                postalCodeFormatError = { postalCode: dromTomNotSupported };

                break;
            case EPostalCodeValidationError.Invalid:
                postalCodeFormatError = { postalCode: invalid };

                break;
            case EPostalCodeValidationError.None:
            default:
                break;
        }

        const errors: Record<string, IResolverError> = {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            ...(!values.street && { street: required }),
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            ...(!values.postalCode && { postalCode: required }),
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            ...(!values.city && { city: required }),
            ...postalCodeFormatError,
        };

        return {
            values: Object.keys(errors).length === 0 ? values : {},
            errors,
        };
    };

    const formProviderProps = useForm<TFormValues>({
        mode: 'all',
        defaultValues: formValues,
        ...(activeView === EActiveView.ClosestLocationSearch && { resolver }),
    });

    const ActiveView = useMemo(() => getActiveView(activeView), [activeView]);

    return <FormProvider {...formProviderProps}>{ActiveView}</FormProvider>;
};

export default AppContent;
